import { GuestDocumentLiteModel } from 'api-hooks/cro/model';
import { useGetGuestDocuments } from 'api-hooks/cro/query';
import { NavigationRoutes } from 'common/constants';
import { DocumentTypeEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import PaginationComponent from 'components/elements/pagination-component';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import Text from 'components/elements/text';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { CardContainer, TableContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

// const monthToMilisecond = 6 * 30 * 24 * 60 * 60 * 1000;

interface DocumentScheduleTableProps {
  documentType?: DocumentTypeEnum;
}

export default function DocumentScheduleTable(
  props: DocumentScheduleTableProps,
) {
  const { documentType = DocumentTypeEnum.Passport } = props;
  const { t } = useTranslation();
  const formatDate = useDate();

  const [page, setPage] = React.useState(1);
  const month = 6;
  const isPassport = documentType === DocumentTypeEnum.Passport;

  const _columns = React.useMemo<IColumn<GuestDocumentLiteModel>[]>(() => {
    return [
      {
        accessorKey: 'guest',
        header: t('common:guest'),
        sortName: 'guest_name',
        minSize: 180,
        cell: ({ row }) => {
          return row.original.guest.name;
        },
      },
      ...(isPassport
        ? []
        : [
            {
              accessorKey: 'type',
              header: t('common:type'),
              cell: ({ row }) => {
                return row.original?.type ?? row.original?.visaDocument?.name;
              },
            },
            {
              accessorKey: 'number',
              header: t('common:number'),
            },
          ]),
      {
        accessorKey: 'expiredAt',
        header: t('common:expired_at'),
        sortName: 'expired_at',
        cell({ row }) {
          const expiredAt = row.original.expiredAt;
          return expiredAt ? formatDate(expiredAt, 'dd MMM yyyy') : '-';
        },
      },
    ];
  }, [formatDate, isPassport, t]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    extras: [, { columns }],
  } = useComposedQuery(
    useGetGuestDocuments,
    {
      document_type: documentType,
      month,
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const permission = useGetCrudPermission('guests');

  return (
    <CardContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text variant="LabelLarge">
          {t('common:document_renewal_reminder', {
            document: t(`common:${documentType}`),
            month,
          })}
        </Text>
      </div>
      <Separator gap={16} />
      <TableContainer
        $style={{
          maxHeight: '300px',
        }}
      >
        <TableComponent
          columns={columns}
          isLoading={isLoading || isFetching}
          data={data?.data || []}
          rowLink={(row) => {
            if (!permission.view) return undefined;
            return {
              pathname: NavigationRoutes.GuestsView,
              query: { id: row.original.guest.id },
            };
          }}
        />
      </TableContainer>
      <PaginationComponent
        page={page}
        onPageChange={(page) => {
          setPage(page);
        }}
        meta={data?.meta}
      />
    </CardContainer>
  );
}
